import React, { useState, useEffect } from "react";
import { Table, DatePicker, Select, Space, Card, Button, message } from "antd";
import { useGetJobs, useGetGrindorSlugs } from "../../../api/grindor";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { ReloadOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface GrindorJobsParams {
  customer?: string;
  product?: string;
  version?: number;
}

const columns = [
  {
    title: "Job ID",
    dataIndex: "job_id",
    key: "job_id",
  },
  {
    title: "Slug",
    dataIndex: "slug",
    key: "slug",
  },
  {
    title: "State",
    dataIndex: "state",
    key: "state",
  },
  {
    title: "Created At",
    dataIndex: "time_created",
    key: "time_created",
    render: (text: string) => dayjs(text).format("YYYY-MM-DD HH:mm:ss"),
  },
  {
    title: "Updated At",
    dataIndex: "time_updated",
    key: "time_updated",
    render: (text: string) => dayjs(text).format("YYYY-MM-DD HH:mm:ss"),
  },
  {
    title: "Warehouse",
    dataIndex: "warehouse",
    key: "warehouse",
  },
  {
    title: "Warehouse Group",
    dataIndex: "warehouse_group",
    key: "warehouse_group",
  },
  {
    title: "Full Refresh",
    dataIndex: "full_refresh",
    key: "full_refresh",
    render: (value: boolean) => (value ? "Yes" : "No"),
  },
];

const stateOptions = [
  { value: "running", label: "Running" },
  { value: "pending", label: "Pending" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
];

export const GrindorJobs: React.FC<GrindorJobsParams> = ({
  customer,
  product,
  version,
}) => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [selectedState, setSelectedState] = useState<string | undefined>(
    undefined
  );
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(
    undefined
  );

  const startDate = dateRange?.[0]?.format("YYYY-MM-DD");
  const endDate = dateRange?.[1]?.format("YYYY-MM-DD");

  const { data: slugsData, loading: slugsLoading } = useGetGrindorSlugs(
    customer || "",
    product || "",
    version || -1
  );

  const {
    data: jobsData,
    loading: jobsLoading,
    refresh: jobsRefresh,
    error: jobsError,
  } = useGetJobs(
    customer || "",
    product || "",
    startDate,
    endDate,
    selectedState,
    version,
    selectedSlug
  );

  useEffect(() => {
    if (jobsError) {
      message.error(jobsError.message || "Failed to fetch jobs", 5);
    }
  }, [jobsError]);

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space>
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setDateRange([dates[0] as Dayjs, dates[1] as Dayjs]);
              } else {
                setDateRange(null);
              }
            }}
          />
          <Select
            placeholder="Select State"
            style={{ width: 200 }}
            allowClear
            onChange={(value) => setSelectedState(value)}
          >
            {stateOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Select Slug"
            style={{ width: 200 }}
            allowClear
            loading={slugsLoading}
            onChange={(value) => setSelectedSlug(value)}
          >
            {slugsData?.slugs?.map((slug: string) => (
              <Option key={slug} value={slug}>
                {slug}
              </Option>
            ))}
          </Select>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => jobsRefresh()}
            loading={jobsLoading}
          >
            Refresh
          </Button>
        </Space>
        <Table
          dataSource={jobsData?.jobs}
          columns={columns}
          loading={jobsLoading}
          rowKey="job_id"
          pagination={{ pageSize: 10 }}
        />
      </Space>
    </Card>
  );
};
